import type { Acoustics } from "@/engine/media/acoustics";
import type { EntryStage } from "@/engine/state/entryStage";
import type { RoomMode } from "@/engine/state/roomControl";

/**
 * What a room of each kind is, before anything has been fetched.
 *
 * A room has to know what it opens on *in order to* know what to fetch: the
 * stage decides whether the scene chunk is asked for at all, and a mode whose
 * stage arrived with its plugin would cost a round trip before the first paint
 * and a flash after it. So the cheap half of a mode is static and the expensive
 * half is the plugin — the profile is four fields and no code, and it lives
 * here rather than in the engine because *which modes exist* is not the
 * engine's business (#607).
 *
 * The `mix` and the screens of a mode that has them are in `<mode>/index.tsx`,
 * behind `registry.ts`.
 */
export interface RoomModeProfile {
  /** Where a visitor lands, before low power or their own choice is applied. */
  stage: EntryStage;
  /** True for a mode that has a campfire to go to at all. The map has none. */
  hasScene: boolean;
  /**
   * Whether this mode places voices or simply carries them.
   *
   * Static because it is what the listener's own acoustics switch means: a mode
   * that places voices has something to switch off, and a mode that divides the
   * room has nothing (#667). The centres it places them around are not static
   * and are not here — a room may hold several, and which is the plugin's to
   * decide, frame by frame if it wants.
   */
  acoustics: Acoustics;
  /**
   * True when the grid redistributes people as they come and go. Bubbles is
   * the exception: who stands where is the whole of what it is about.
   */
  redistribute: boolean;
}

const PROFILES: Record<RoomMode, RoomModeProfile> = {
  // The room the product is about: the campfire, the only mode with a fire.
  campfire: {
    stage: "campfire",
    hasScene: true,
    redistribute: true,
    acoustics: "spatial",
  },
  // The square map (#913): no campfire, walked rather than dealt, and who
  // stands where is the whole of what it is about — so nobody is re-spaced.
  bubbles: {
    stage: "map",
    hasScene: false,
    redistribute: false,
    acoustics: "spatial",
  },
  // A plain call has no campfire to go to, and no mode's code to fetch.
  plain: {
    stage: "grid-plain",
    hasScene: false,
    redistribute: true,
    acoustics: "flat",
  },
  // One grid of everybody is three grids of three sets of people.
  // Flat today, and the mode most likely to stop being: the hall carries
  // voices while a team could place them around a centre of its own.
  teams: {
    stage: "grid-areas",
    hasScene: false,
    redistribute: true,
    acoustics: "flat",
  },
};

/** The profile of a mode, defaulting to the campfire a room opens in. */
export function profileOf(mode: RoomMode | undefined): RoomModeProfile {
  return PROFILES[mode ?? "campfire"];
}
